import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import { useUserStore } from '@/store/userStore';
import { startDay, type MarkStatus, type StartMark } from '@/lib/dofiStart';

/**
 * Прогресс «Старта с Дофи»: дата старта (profiles.dofi_start_at) и отметки
 * заданий (dofi_start_marks, миграция 068). Отметки живут только в приложении —
 * в контекст ИИ они не уходят (иначе правка согласия 4.2).
 */
export function useDofiStart() {
  const { profile, setProfile } = useUserStore();
  const [marks, setMarks] = useState<StartMark[]>([]);
  const [loaded, setLoaded] = useState(false);
  const startAt = (profile as any)?.dofi_start_at as string | null | undefined;

  const reload = useCallback(async () => {
    if (!profile?.id || !startAt) { setLoaded(true); return; }
    const { data, error } = await supabase
      .from('dofi_start_marks')
      .select('day,status,choice,note')
      .eq('user_id', profile.id);
    if (!error && data) setMarks(data as StartMark[]);
    setLoaded(true);
  }, [profile?.id, startAt]);

  useEffect(() => { reload(); }, [reload]);

  async function start(): Promise<boolean> {
    if (!profile?.id || !(await requireSession())) return false;
    const { data, error } = await supabase
      .from('profiles')
      .update({ dofi_start_at: new Date().toISOString() })
      .eq('id', profile.id)
      .select()
      .single();
    if (error) { console.warn('[DofiStart] start failed:', error); return false; }
    if (data) setProfile(data);
    return true;
  }

  async function mark(day: 1 | 2 | 3, patch: { status?: MarkStatus; choice?: string | null; note?: string | null }) {
    if (!profile?.id) return;
    const prev = marks.find((m) => m.day === day);
    const row = {
      user_id: profile.id,
      day,
      status: patch.status ?? prev?.status ?? 'done',
      choice: patch.choice !== undefined ? patch.choice : prev?.choice ?? null,
      note: patch.note !== undefined ? patch.note : prev?.note ?? null,
      updated_at: new Date().toISOString(),
    };
    // Оптимистично: кнопка отвечает сразу
    setMarks((ms) => [...ms.filter((m) => m.day !== day), { day, status: row.status, choice: row.choice, note: row.note }]);
    const { error } = await supabase.from('dofi_start_marks').upsert(row);
    if (error) { console.warn('[DofiStart] mark failed:', error); reload(); }
  }

  return { startAt: startAt ?? null, day: startDay(startAt), marks, loaded, start, mark, reload };
}
